"use client";

import { FC } from "react";
import { Modal } from "antd";
import PrimaryButton from "@/components/ui/PrimaryButton";
import type { TutorialMission } from "@/constants/tutorialMissions";
import { TUTORIAL_CLEAR_MODAL_CLASS } from "@/lib/tutorial/dom";

export interface TutorialClearItem {
  key: string;
  title: string;
  rewardPoint: number;
}

interface Props {
  items: readonly TutorialClearItem[];
  clearedCount: number;
  total: number;
  /** 次にやるとよいミッション (無ければ「次のミッションへ」を出さない) */
  next: TutorialMission | null;
  onNext: (mission: TutorialMission) => void;
  onClose: () => void;
}

/**
 * チュートリアルミッションのクリアのお祝い。見た目はホームのミッションクリア (MissionClearModal) に揃える。
 * 登録のミッションのように同時にいくつかクリアしたときは、まとめて 1 回で出す。
 */
const TutorialClearModal: FC<Props> = ({
  items,
  clearedCount,
  total,
  next,
  onNext,
  onClose,
}) => {
  const open = items.length > 0;
  const totalPoint = items.reduce((sum, item) => sum + item.rewardPoint, 0);
  const allCleared = clearedCount >= total;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={320}
      zIndex={1080}
      wrapClassName={TUTORIAL_CLEAR_MODAL_CLASS}
      styles={{
        content: {
          borderRadius: "var(--radius-modal, 8px)",
          padding: "28px 24px 20px",
        },
      }}
    >
      {open && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 40, lineHeight: 1 }} aria-hidden>
            {allCleared ? "🏆" : "🎉"}
          </div>
          <div
            style={{
              fontFamily: "var(--font-mincho)",
              fontSize: 18,
              fontWeight: 500,
              color: "var(--color-text-primary, #3a3a3a)",
              letterSpacing: "0.12em",
            }}
          >
            ミッションクリア！
          </div>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              fontSize: 13,
              color: "var(--color-text-secondary, #8e8e8e)",
              lineHeight: 1.6,
            }}
          >
            {items.map((item) => (
              <li key={item.key}>{item.title}</li>
            ))}
          </ul>
          <div
            style={{
              width: "100%",
              background: "linear-gradient(135deg,#e6f5ee 0%,#fffbe0 45%,#fdd8dc 100%)",
              borderRadius: 8,
              padding: "12px 16px",
              fontSize: 14,
              fontWeight: 500,
              color: "var(--color-text-primary, #3a3a3a)",
            }}
          >
            報酬: {totalPoint}pt
          </div>
          <div style={{ fontSize: 12, color: "#a08a4f", letterSpacing: "0.04em" }}>
            {allCleared
              ? `チュートリアルをすべてクリアしました（${total} / ${total}）`
              : `チュートリアル ${clearedCount} / ${total} クリア`}
          </div>
          {next && !allCleared ? (
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
              <PrimaryButton
                variant="filled"
                block
                spacing="normal"
                onClick={() => onNext(next)}
              >
                次のミッションへ
              </PrimaryButton>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary, #8e8e8e)" }}>
                次: {next.title}（+{next.reward}pt）
              </div>
              <PrimaryButton block spacing="normal" onClick={onClose}>
                あとで
              </PrimaryButton>
            </div>
          ) : (
            <PrimaryButton variant="filled" block spacing="normal" onClick={onClose}>
              OK
            </PrimaryButton>
          )}
        </div>
      )}
    </Modal>
  );
};

export default TutorialClearModal;
